import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Group, Panel, Separator } from 'react-resizable-panels'
import { DisplayWindow, DisplayHeader, Display } from '../display'
import SearchroomChat from '../chat/SearchroomChat'
import ItemPdfHeader from './ItemPdfHeader'
import PdfViewer from '../pdf/PdfViewer'
import { standardPdfUrl } from '../../services/pdfs'
import { standardId } from '../../utils/standardRef'
import './SearchroomDisplay.css'

const svgProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
}

const IconSearch = () => (
  <svg {...svgProps}>
    <circle cx="11" cy="11" r="7" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
)

// Double chevron used for the pane collapse/expand toggles; `dir` points toward
// the direction the visible pane grows (right = grow chat, left = grow viewer).
const IconChevrons = ({ dir }) => (
  <svg {...svgProps}>
    {dir === 'right' ? (
      <>
        <polyline points="7 6 13 12 7 18" />
        <polyline points="13 6 19 12 13 18" />
      </>
    ) : (
      <>
        <polyline points="17 6 11 12 17 18" />
        <polyline points="11 6 5 12 11 18" />
      </>
    )}
  </svg>
)

// The inner chat | PDF split for one searchroom: message chat on the left, the
// item PDF viewer on the right, with per-pane collapse toggles and an in-chat
// search overlay. Reusable window content - the room title and edit/delete live
// in the surrounding window header. The split sizes persist via `onPanesChange`
// (stored on the grid window, so nothing leaks into standalone layout keys).
function SearchroomPanes({ searchroom, panes, onPanesChange }) {
  const defaultLayout = useMemo(
    () => ({ chat: panes?.chat ?? 50, viewer: panes?.viewer ?? 50 }),
    [panes]
  )

  const items = searchroom?.items || []
  const itemIds = items.map((it) => it._id).join(',')
  const [selectedItemId, setSelectedItemId] = useState('')
  const [searchOpen, setSearchOpen] = useState(false)
  // A reference opened from a chat answer. When set, the viewer shows the FULL
  // standard scrolled to the item (vs. the dropdown's item-scoped view), and the
  // header shows that standard and section. Cleared when the user picks from
  // the room's item dropdown.
  const [refView, setRefView] = useState(null)

  const openItem = useCallback((item) => {
    if (!item?.standard) return
    setRefView({
      _id: item._id,
      standard: item.standard,
      label: item.label || '',
      page: item.pageStart,
    })
  }, [])

  const selectFromHeader = useCallback((id) => {
    setRefView(null)
    setSelectedItemId(id)
  }, [])

  // Pane collapse: each header toggle maximizes its own pane by collapsing the
  // other. `collapsed` is derived from onResize so it reflects manual drags and
  // the persisted layout too. 'none' | 'chat' | 'viewer'.
  const chatPanelRef = useRef(null)
  const viewerPanelRef = useRef(null)
  const [collapsed, setCollapsed] = useState('none')

  const onChatResize = useCallback((size) => {
    setCollapsed((c) => (size.asPercentage <= 0.5 ? 'chat' : c === 'chat' ? 'none' : c))
  }, [])
  const onViewerResize = useCallback((size) => {
    setCollapsed((c) => (size.asPercentage <= 0.5 ? 'viewer' : c === 'viewer' ? 'none' : c))
  }, [])

  const toggleChatMaximize = () => {
    if (collapsed === 'viewer') viewerPanelRef.current?.expand()
    else viewerPanelRef.current?.collapse()
  }
  const toggleViewerMaximize = () => {
    if (collapsed === 'chat') chatPanelRef.current?.expand()
    else chatPanelRef.current?.collapse()
  }

  const roomId = searchroom?._id
  useEffect(() => {
    setSearchOpen(false)
    setRefView(null)
  }, [roomId])

  // Keep the viewer's selection valid as the item set changes (room switch or
  // edit add/remove): keep the current pick if still present, else first item.
  useEffect(() => {
    setSelectedItemId((prev) =>
      prev && items.some((it) => it._id === prev) ? prev : items[0]?._id || ''
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemIds])

  const selectedItem = items.find((item) => item._id === selectedItemId) || null

  return (
    <div className="searchroom-display">
      <Group
        orientation="horizontal"
        className="searchroom-panelgroup"
        defaultLayout={defaultLayout}
        onLayoutChanged={onPanesChange}
      >
        <Panel
          id="chat"
          className="searchroom-pane"
          defaultSize="50%"
          minSize="25%"
          collapsible
          collapsedSize="0%"
          panelRef={chatPanelRef}
          onResize={onChatResize}
        >
          <DisplayWindow
            header={
              <DisplayHeader
                title="Chat"
                actions={
                  <>
                    <button
                      type="button"
                      title={collapsed === 'viewer' ? 'Restore split view' : 'Expand chat pane'}
                      aria-label={
                        collapsed === 'viewer' ? 'Restore split view' : 'Expand chat pane'
                      }
                      className={collapsed === 'viewer' ? 'is-active' : ''}
                      onClick={toggleChatMaximize}
                    >
                      <IconChevrons dir={collapsed === 'viewer' ? 'left' : 'right'} />
                    </button>
                    <button
                      type="button"
                      title="Search messages"
                      aria-label="Search messages"
                      className={searchOpen ? 'is-active' : ''}
                      onClick={() => setSearchOpen((open) => !open)}
                    >
                      <IconSearch />
                    </button>
                  </>
                }
              />
            }
          >
            <Display fill>
              <SearchroomChat
                searchroomId={searchroom?._id}
                searchOpen={searchOpen}
                onCloseSearch={() => setSearchOpen(false)}
                onOpenItem={openItem}
              />
            </Display>
          </DisplayWindow>
        </Panel>

        <Separator className="searchroom-resize-handle" />

        <Panel
          id="viewer"
          className="searchroom-pane"
          defaultSize="50%"
          minSize="25%"
          collapsible
          collapsedSize="0%"
          panelRef={viewerPanelRef}
          onResize={onViewerResize}
        >
          <DisplayWindow
            header={
              <DisplayHeader
                actions={
                  <button
                    type="button"
                    title={collapsed === 'chat' ? 'Restore split view' : 'Expand PDF pane'}
                    aria-label={collapsed === 'chat' ? 'Restore split view' : 'Expand PDF pane'}
                    className={collapsed === 'chat' ? 'is-active' : ''}
                    onClick={toggleViewerMaximize}
                  >
                    <IconChevrons dir={collapsed === 'chat' ? 'right' : 'left'} />
                  </button>
                }
              >
                <ItemPdfHeader
                  items={items}
                  selectedId={selectedItemId}
                  activeRef={refView}
                  onSelect={selectFromHeader}
                />
              </DisplayHeader>
            }
          >
            <Display fill>
              {refView ? (
                <PdfViewer
                  key={`ref-${standardId(refView.standard)}`}
                  fileUrl={standardPdfUrl(refView.standard)}
                  page={refView.page}
                  standard={standardId(refView.standard)}
                />
              ) : selectedItem ? (
                <PdfViewer
                  key={selectedItem._id}
                  fileUrl={standardPdfUrl(selectedItem.standard)}
                  page={selectedItem.pageStart}
                  pageRange={[selectedItem.pageStart, selectedItem.pageEnd]}
                  standard={standardId(selectedItem.standard)}
                />
              ) : (
                <div className="display-empty">Select an item to view its PDF.</div>
              )}
            </Display>
          </DisplayWindow>
        </Panel>
      </Group>
    </div>
  )
}

export default SearchroomPanes
